import { formatName } from '@pickleballinc/lib/format-name';
import { formatPhoneFromParts } from '@pickleballinc/lib/phone-utils';
import { PlayerSelectCard } from '@pickleballinc/ui/components/global/Cards/PlayerSelectCard';
import { Button } from '@pickleballinc/ui/components/ui/button';

import { User } from '@/entities/SystemUsers';
import { DateUtil } from '@/utils/date';

export interface OrganizationContact {
	uuid?: string;
	userUuid: string;
	organizationUuid: string;
	phoneNumber: string | undefined;
	phone: string | undefined;
	email: string | undefined;
	firstName: string | undefined;
	lastName: string | undefined;
	suffixName: string | undefined;
	isPrimary: boolean | undefined;
	showAgeToPublic?: boolean | undefined;
	dob?: string;
	gender?: string;
	singlesRating?: number;
	doublesRating?: number;
	skinnySinglesRating?: number;
	profilePictureUrl?: string;
	countryAbbreviation?: string;
	stateTitle?: string;
	showStateToPublic?: boolean;
	internationalCountryCallingCode?: string;
}

export interface CustomUser extends OrganizationContact {
	state?: string;
	country?: string;
}

type UserCardProps = {
	user: CustomUser | User | Contact;
	isAdding?: boolean;
	showRating?: boolean;
	add: (param: any) => void;
	index?: number;
	addingUser?: string | null;
	renderPayOption?: boolean;
	hideButton?: boolean;
};

export interface Contact {
	uuid?: string | null;
	userUuid: string;
	moduleUuid: string;
	moduleFor: number;
	firstName: string;
	lastName: string;
	suffixName?: string | null;
	isPrimary: boolean;
	dateCreated?: Date | null;
	dateDisabled?: Date | null;
	dateUpdated?: Date | null;
	phoneNumber: string;
	phone?: string;
	email: string;
	showAgeToPublic?: boolean | undefined;
	dob?: string;
	gender?: string;
	singlesRating?: number;
	doublesRating?: number;
	skinnySinglesRating?: number;
	profilePictureUrl?: string;
	countryAbbreviation?: string;
	stateTitle?: string;
	showStateToPublic?: string;
	internationalCountryCallingCode?: string;
}

export function UserCard({ user, add, showRating, addingUser, hideButton }: UserCardProps) {
	const normalizedUser = {
		...user,
		phoneNumber: 'phoneNumber' in user ? user.phoneNumber : user.phone,
		internationalCountryCallingCode: 'internationalCallingCode' in user ? user.internationalCallingCode : ''
	};

	const dobModified = user?.dob ? DateUtil.calculateYearsPassedFrom(new Date(user.dob)) : user?.dob;

	let age: number | string | undefined;
	if (user?.dob) age = user.showAgeToPublic ? DateUtil.calculateYearsPassedFrom(new Date(user.dob)) : '**';

	const isAddingThisUser = !!addingUser && addingUser === user?.uuid;
	const ratingCandidates = [
		{ label: 'D', value: user?.doublesRating },
		{ label: 'S', value: user?.singlesRating },
		{ label: 'SS', value: user?.skinnySinglesRating }
	];
	// Unrated values were left blank on this card (never "NR"), so only the rated ones are passed.
	const ratings = showRating ? ratingCandidates.filter((rating) => !!rating.value && rating.value > 0) : undefined;

	return (
		<div>
			{user ? (
				<PlayerSelectCard
					color="orange"
					name={formatName({ firstName: user.firstName, lastName: user.lastName })}
					avatarUrl={user.profilePictureUrl}
					state={user.showStateToPublic ? user.stateTitle : undefined}
					country={user.countryAbbreviation}
					age={age}
					gender={user.gender === 'M' ? 'Male' : 'Female'}
					email={user.email}
					phone={formatPhoneFromParts({
						internationalCountryCallingCode: normalizedUser.internationalCountryCallingCode ?? '',
						phone: normalizedUser.phoneNumber ?? ''
					})}
					ratings={ratings}
					ratingDecimals={2}
					selected={!!hideButton}
					action={
						hideButton
							? undefined
							: {
									label: isAddingThisUser ? 'Loading...' : 'Select',
									onClick: () => add({ ...user, dob: dobModified }),
									disabled: isAddingThisUser
								}
					}
					actionSlot={
						hideButton ? (
							<Button
								className="w-fit bg-gray-600 hover:bg-gray-600 !px-2.5 !py-2 !h-fit !rounded-md cursor-not-allowed"
								disabled={isAddingThisUser}
							>
								<p className="text-xs font-semibold text-white">{isAddingThisUser ? 'Loading...' : 'Selected'}</p>
							</Button>
						) : undefined
					}
				/>
			) : null}
		</div>
	);
}
